import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import { formatLocal, parseUtc } from "./socFreshness";
import { SeverityBadge, buttonClass } from "./socUi";

// What each External threat event figure is, and where its events come from.
export const EXTERNAL_EVENT_BANDS = {
  esmEvents: { label: "Attack-surface findings", source: "Surface scan" },
  darkWebEvents: { label: "Dark web exposures", source: "Dark web monitoring" },
  secretsEvents: { label: "Exposed secrets", source: "Secrets scan" },
  brandEvents: { label: "Brand threats", source: "Brand protection" },
  ransomwareEvents: { label: "Ransomware activity", source: "Ransomware monitoring" },
  // Not an event band: the brand threats still open (New, Under review, Takedown requested), whatever
  // their age, read from their own route.
  openBrandThreats: { label: "Brand threats", source: "Brand protection", open: true },
};

const pageUrl = (band, page) => (EXTERNAL_EVENT_BANDS[band]?.open
  ? `/soc/brand-threats?page=${page}`
  : `/soc/external-events?band=${encodeURIComponent(band)}&page=${page}`);

const SEVERITY_KEYS = { critical: "critical", high: "high", medium: "medium", low: "low" };
const severityKey = (s) => SEVERITY_KEYS[String(s || "").toLowerCase()] || "unrated";

// The events behind one External threat figure (/soc), listed under the tile: the band's events in the
// tile's 30-day window, newest first, from GET /soc/external-events. A page at a time.
const SocExternalEvents = ({ band, onClose }) => {
  const apiCall = useApi();
  const [page, setPage] = useState(1);
  // undefined: loading; null: failed; else the page.
  const [data, setData] = useState(undefined);
  const [attempt, setAttempt] = useState(0);
  const meta = EXTERNAL_EVENT_BANDS[band] || { label: band, source: "" };

  useEffect(() => { setPage(1); }, [band]);

  useEffect(() => {
    let live = true;
    setData(undefined);
    apiCall("GET", pageUrl(band, page))
      .then((d) => { if (live) setData(d || null); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [apiCall, band, page, attempt]);

  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || 25))) : 1;

  let body;
  if (data === undefined) {
    body = (
      <div className="flex justify-center py-8" data-testid="soc-external-events-loading">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-accent-cyan" />
      </div>
    );
  } else if (data === null) {
    body = (
      <div className="py-4 text-sm text-content-secondary" data-testid="soc-external-events-failed">
        These events couldn't be loaded.
        <button type="button" onClick={() => setAttempt((a) => a + 1)} className={`ml-2 ${buttonClass("sm")}`}>Retry</button>
      </div>
    );
  } else if ((data.items || []).length === 0) {
    body = (
      <p className="py-6 text-center text-sm text-content-muted" data-testid="soc-external-events-empty">
        {meta.open ? "No open brand threats." : "No events in the last 30 days."}
      </p>
    );
  } else {
    body = (
      <>
        <div className="max-h-[32rem] overflow-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm" data-testid="soc-external-events-table">
            <thead className="sticky top-0 z-10 bg-theme-card-solid shadow-[0_1px_0_var(--border-color)]">
              <tr className="text-xs text-content-muted">
                <th className="py-1.5 pl-3 pr-3 font-medium">{meta.open ? "Discovered" : "When"}</th>
                <th className="py-1.5 pr-3 font-medium">Severity</th>
                <th className="py-1.5 pr-3 font-medium">Event</th>
                <th className="py-1.5 pr-3 font-medium">Domain</th>
                <th className="py-1.5 pr-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.id} className="border-t border-border align-top" data-testid="soc-external-event">
                  <td className="whitespace-nowrap py-2 pl-3 pr-3 text-content-secondary">{formatLocal(parseUtc(e.eventTimestamp))}</td>
                  <td className="py-2 pr-3"><SeverityBadge severity={severityKey(e.severity)} /></td>
                  <td className="py-2 pr-3">
                    <p className="font-medium text-content-primary">{e.title}</p>
                    {e.description && <p className="mt-0.5 text-xs text-content-muted">{e.description}</p>}
                  </td>
                  <td className="break-all py-2 pr-3 text-content-secondary">{e.domain}</td>
                  <td className="py-2 pr-3 text-content-secondary">{e.sourceSystem}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-content-muted">
          <span data-testid="soc-external-events-count">{`${data.total} ${data.total === 1 ? "event" : "events"} · page ${data.page} of ${pages}`}</span>
          <button type="button" className={buttonClass("sm")} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <button type="button" className={buttonClass("sm")} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </>
    );
  }

  return (
    <div data-testid={`soc-external-events-${band}`} id="soc-external-events" className="scroll-mt-4">
      <Card>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-content-primary">{`${meta.label} · ${meta.open ? "open now" : "new in the last 30 days"}`}</h3>
            {meta.source && <p className="text-xs text-content-muted">{`From ${meta.source}`}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close events" className={`${buttonClass("sm")} !px-2`}>
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {body}
      </Card>
    </div>
  );
};

export default SocExternalEvents;
